/**
 * useOpenToItem — the pinned pane's open-to-item address (story pinned-pane,
 * AC-9).
 *
 * TWO ADDRESSING MECHANISMS, ONE PARSER:
 *   1. `?item=<instance_id>` on the taskpane URL — what an in-pane deep link
 *      uses, and what works in the plain-browser dev mode (which is what makes
 *      this testable at all).
 *   2. The Office roaming setting `ora.pinnedPane.openToItem` — what another
 *      add-in surface or a host-side handoff can set. Read ONCE and CLEARED, so
 *      a stale address cannot re-hijack the next open.
 * A third mechanism would be one more call site in `readAddress`, not a second
 * resolution rule.
 *
 * WHY THE QUEUE READ ALONE CANNOT ANSWER THIS: an already-answered item is by
 * construction ABSENT from `list_waiting_for_me`, so a missing row is ambiguous
 * between "answered", "does not exist" and "belongs to another AM". The second
 * read disambiguates, and it is the EXISTING `getStatus` →
 * `get_workflow_status` path — no new tool, no new endpoint, no client-side
 * inference.
 *
 * WHAT IT REFUSES TO DISTINGUISH: "another AM's run" and "does not exist" MUST
 * render the same copy. `get_workflow_status` answers `not_found` — never
 * `forbidden` — for an out-of-scope instance precisely so the caller cannot
 * tell those apart, and telling them apart here would put the leak back in the
 * client. A failed second read (network, timeout) falls back to the SAME
 * unknown copy: the safe direction reveals less.
 */

import { useEffect, useRef, useState } from "react";

/** Office roaming-settings key. One key, read once, cleared after use. */
export const OPEN_TO_ITEM_ROAMING_KEY = "ora.pinnedPane.openToItem";
/** Query-parameter name on the taskpane SourceLocation. */
export const OPEN_TO_ITEM_QUERY_PARAM = "item";

export type OpenToItemOutcome =
  /** No address was supplied — the pane just opens on the queue. */
  | "none"
  /** An address was found and is still being resolved. */
  | "resolving"
  /** The address is a row in THIS AM's queue — select it. */
  | "in_queue"
  /** The run exists and this AM may see it, so it is simply no longer waiting. */
  | "answered"
  /** Unknown, or another AM's. Deliberately indistinguishable. */
  | "unknown";

/** BINDING copy for the two fallback outcomes. */
export const OPEN_TO_ITEM_ANSWERED_COPY =
  "That item has already been answered — showing your queue instead.";
export const OPEN_TO_ITEM_UNKNOWN_COPY =
  "We couldn't open that item — showing your queue instead.";

export interface UseOpenToItemOptions {
  /** Instance ids currently in the queue, in the server's order. */
  queueInstanceIds: string[];
  /** True once the queue read has succeeded at least once. */
  queueLoaded: boolean;
  /** The EXISTING per-instance status read. Resolves ⇒ visible to this AM. */
  getStatus: (instanceId: string) => Promise<unknown>;
  /** Disables the address read entirely (tests). */
  disabled?: boolean;
}

export interface UseOpenToItemResult {
  /** The address that was found, or null when there was none. */
  instanceId: string | null;
  outcome: OpenToItemOutcome;
  /** The plain statement to render, or null when there is nothing to say. */
  message: string | null;
}

/** Read the `?item=` parameter. Works in a plain browser and inside a host. */
function readQueryAddress(): string | null {
  try {
    if (typeof window === "undefined" || !window.location?.search) return null;
    const value = new URLSearchParams(window.location.search).get(
      OPEN_TO_ITEM_QUERY_PARAM,
    );
    const trimmed = (value ?? "").trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

/**
 * Read the roaming setting and CLEAR it in the same pass. Defensive
 * throughout: `Office` is undefined in a plain browser and under jsdom, and
 * `saveAsync` is absent on some hosts.
 */
function readAndClearRoamingAddress(): string | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const settings = (globalThis as any)?.Office?.context?.roamingSettings;
    if (!settings || typeof settings.get !== "function") return null;
    const raw = settings.get(OPEN_TO_ITEM_ROAMING_KEY);
    const value = typeof raw === "string" ? raw.trim() : "";
    if (value.length === 0) return null;
    try {
      settings.remove?.(OPEN_TO_ITEM_ROAMING_KEY);
      settings.saveAsync?.(() => undefined);
    } catch {
      // Clearing is best-effort. Worst case the address is re-read on the next
      // open, which is why the resolution below is idempotent.
    }
    return value;
  } catch {
    return null;
  }
}

/** The ONE parser. Query parameter wins; the roaming setting is the fallback. */
export function readOpenToItemAddress(): string | null {
  return readQueryAddress() ?? readAndClearRoamingAddress();
}

export function useOpenToItem(
  options: UseOpenToItemOptions,
): UseOpenToItemResult {
  const { queueInstanceIds, queueLoaded, getStatus, disabled = false } = options;

  // Read the address ONCE per mount — before any await, so a re-render cannot
  // re-read a roaming setting we already cleared.
  const [instanceId] = useState<string | null>(() =>
    disabled ? null : readOpenToItemAddress(),
  );
  const [outcome, setOutcome] = useState<OpenToItemOutcome>(
    instanceId ? "resolving" : "none",
  );

  const getStatusRef = useRef(getStatus);
  getStatusRef.current = getStatus;

  /**
   * SETTLED means the answer is IN, not that a request left the building.
   *
   * It used to be set the moment the second read STARTED, which made an
   * ordinary re-render fatal: `queueInstanceIds` is a fresh array on every 30s
   * queue poll, so a poll landing mid-flight re-ran this effect, the re-run's
   * guard saw `settledRef` already true and bailed without issuing a
   * replacement, AND the first run's cleanup set its own `cancelled` flag — so
   * the request still in flight had its eventual result thrown away by the very
   * re-render that refused to replace it. The pane then sat at "resolving"
   * forever, precisely under the slow backend this feature is for.
   *
   * `inFlightRef` is what actually prevents a duplicate concurrent read for the
   * SAME id; `settledRef` only ever flips once a real outcome exists.
   */
  const settledRef = useRef(false);
  const inFlightRef = useRef<string | null>(null);

  /** Unmount only — NEVER a re-run. A re-run must not discard a live result. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!instanceId || disabled || settledRef.current) return;
    // The queue read is the FIRST of the two reads. Resolving before it lands
    // would misreport an in-queue item as unknown.
    if (!queueLoaded) return;

    if (queueInstanceIds.includes(instanceId)) {
      // A later queue poll that DOES contain the row is a real answer and
      // supersedes any read still in flight — dropping the token makes the
      // in-flight resolution below a no-op rather than an overwrite.
      inFlightRef.current = null;
      settledRef.current = true;
      setOutcome("in_queue");
      return;
    }

    // Already reading this exact id: leave that request alone. Re-issuing would
    // duplicate it; cancelling it would strand the pane.
    if (inFlightRef.current === instanceId) return;

    inFlightRef.current = instanceId;
    void (async () => {
      try {
        await getStatusRef.current(instanceId);
        // A success means the run exists AND this AM may see it — so it is
        // simply no longer waiting.
        if (!mountedRef.current || inFlightRef.current !== instanceId) return;
        inFlightRef.current = null;
        settledRef.current = true;
        setOutcome("answered");
      } catch {
        // `not_found` (does not exist OR another AM's), and every transport
        // failure, land on the SAME copy. Never the answered copy: that would
        // confirm the existence of a run this AM may not see.
        if (!mountedRef.current || inFlightRef.current !== instanceId) return;
        inFlightRef.current = null;
        settledRef.current = true;
        setOutcome("unknown");
      }
    })();
  }, [instanceId, disabled, queueLoaded, queueInstanceIds]);

  const message =
    outcome === "answered"
      ? OPEN_TO_ITEM_ANSWERED_COPY
      : outcome === "unknown"
        ? OPEN_TO_ITEM_UNKNOWN_COPY
        : null;

  return { instanceId, outcome, message };
}

export default useOpenToItem;
